<template>
    <div class="code-field">
        <div class="code-field__inputs">
            <input
                v-for="index in [...Array(length).keys()]"
                :key="index"
                ref="codeInputs"
                :class="['code-field__input', { haserror: hasError }]"
                type="text"
                placeholder=""
                maxlength="1"
                :disabled="disabled"
                :readonly="readonly"
                :value="codeArray[index]"
                @input="onInput(index, $event)"
                @keydown="onKeydown(index, $event)"
                @paste="onPaste(index, $event)"
            />
        </div>
        <div class="error-container">
            <Error v-if="handlesError" :name="name" :value-field="model" />
        </div>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    computed,
    watch,
    onMounted,
    nextTick,
    toRefs,
} from 'vue';
import Error from '@/components/basic/Error.vue';
import { DELETE_ERROR } from '@/constants/mutation-types';
import { useStore } from 'vuex';

export default defineComponent({
    name: 'CodeField',
    components: { Error },
    props: {
        name: {
            type: String,
            default: '',
        },
        value: {
            type: String,
            default: '',
        },
        length: {
            type: Number,
            default: 6,
        },
        disabled: {
            type: [Boolean],
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        handlesError: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const { name, disabled, readonly, handlesError, length, value } =
            toRefs(props);
        const store = useStore();

        const codeArray = ref<string[]>(Array(length.value).fill(''));

        const model = ref(value.value || '');

        const codeInputs = ref<HTMLInputElement[]>([]);

        onMounted(() => {
            if (model.value) {
                codeArray.value = model.value.split('').slice(0, length.value);
            }
            checkIsError();
        });

        const updateModel = () => {
            model.value = codeArray.value.join('');

            emit('update:modelValue', model.value);
            store.commit(DELETE_ERROR, name.value);
        };

        const focusNextInput = (index: number) => {
            if (index < length.value - 1) {
                const nextInput = codeInputs.value[index + 1];
                if (nextInput) {
                    nextTick(() => {
                        nextInput.focus();
                    });
                }
            }
        };

        const onInput = (index: number, event: Event) => {
            const inputEl = event.target as HTMLInputElement;
            const input = inputEl.value;
            codeArray.value[index] = input.charAt(0);
            updateModel();
            if (input.length === 1 && index < length.value - 1) {
                const nextInput = codeInputs.value[index + 1];
                if (nextInput) {
                    nextTick(() => {
                        nextInput.focus();
                    });
                }
            }
        };

        const onKeydown = (index: number, event: KeyboardEvent) => {
            if (
                event.key === 'Backspace' &&
                !codeArray.value[index] &&
                index > 0
            ) {
                codeArray.value[index - 1] = '';
                const previousInput = codeInputs.value[index - 1];
                if (previousInput) {
                    nextTick(() => {
                        previousInput.focus();
                    });
                }
                updateModel();
            }
        };

        const onPaste = (index: number, event: ClipboardEvent) => {
            event.preventDefault();
            const clipboardData =
                event.clipboardData || (window as any).clipboardData;
            if (clipboardData) {
                const pasteData = clipboardData.getData('text');
                const codeToPaste = pasteData.split('').slice(0, length.value);
                codeToPaste.forEach((char: string, idx: number) => {
                    if (idx < length.value) {
                        codeArray.value[idx] = char;
                    }
                });
                updateModel();
                focusNextInput(index);
            }
        };

        const checkIsError = () => {
            const interval = setInterval(() => {
                const error = store.state.errors.get(name.value);
                if (error) {
                    clearInterval(interval);
                }
            }, 50);
        };

        watch(
            () => value.value,
            (newVal: any) => {
                model.value = newVal || '';
                codeArray.value = model.value.split('').slice(0, length.value);
            }
        );

        watch(model, (newVal: string) => {
            emit('update:modelValue', newVal);
        });

        const hasError = computed(() => {
            return !!store.state.errors.get(name.value);
        });

        const isValidFieldsChanged = computed(() => {
            return store.state.isValidFieldsChanged;
        });

        return {
            name,
            disabled,
            readonly,
            handlesError,
            length,
            codeArray,
            model,
            codeInputs,
            onInput,
            onKeydown,
            onPaste,
            hasError,
            isValidFieldsChanged,
        };
    },
});
</script>

<style scoped lang="scss">
.code-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;

    &__inputs {
        display: flex;
        gap: 10px;
    }
}

.error-container {
    position: relative;
    display: flex;
    justify-content: center;
    height: 20px;
}
</style>
